import React from 'react';
import { themes, pageTypeMeta } from './themes';
import { Dumbbell, Clock, Flame, Trophy, Heart, RotateCcw } from 'lucide-react';

const FitnessPage = ({ page, pageIndex, totalPages, theme }) => {
  const t = themes[theme] || themes['modern-minimal'];
  const meta = pageTypeMeta[page.pageType] || { label: 'Fitness', color: t.accent };

  const renderWorkout = () => {
    const exercises = page.exercises || [];
    return (
      <div className="flex-1 overflow-hidden">
        {exercises.length > 0 ? (
          <div className="space-y-1">
            <div className="flex items-center gap-1 pb-1 mb-1" style={{ borderBottom: `1px solid ${t.accent}20` }}>
              <span className="flex-1 text-[7px] font-bold uppercase" style={{ color: t.subtext }}>Exercise</span>
              <span className="w-8 text-[7px] font-bold uppercase text-center" style={{ color: t.subtext }}>Sets</span>
              <span className="w-8 text-[7px] font-bold uppercase text-center" style={{ color: t.subtext }}>Reps</span>
              <span className="w-10 text-[7px] font-bold uppercase text-center" style={{ color: t.subtext }}>Rest</span>
              <span className="w-4 text-[7px] font-bold uppercase text-center" style={{ color: t.subtext }}></span>
            </div>
            {exercises.map((ex, ei) => (
              <div key={ei} className="flex items-center gap-1 py-1 rounded-lg px-1" style={{ backgroundColor: ei % 2 === 0 ? 'transparent' : (t.fieldBg || t.bg) }}>
                <div className="flex-1 min-w-0">
                  <p style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', fontWeight: 600, color: t.text }}>{ex.name}</p>
                  {ex.notes && <p style={{ fontFamily: t.bodyFont, fontSize: '0.45rem', fontStyle: 'italic', color: t.subtext }}>{ex.notes}</p>}
                </div>
                <span className="w-8 text-center" style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', fontWeight: 700, color: t.accent }}>{ex.sets}</span>
                <span className="w-8 text-center" style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', fontWeight: 600, color: t.text }}>{ex.reps}</span>
                <span className="w-10 text-center" style={{ fontFamily: t.bodyFont, fontSize: '0.5rem', color: t.subtext }}>{ex.rest}</span>
                <div className="w-4 h-4 rounded border flex-shrink-0" style={{ borderColor: t.accent + '40' }} />
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-1.5">
            {(page.fields || []).map((f, fi) => (
              <div key={fi} className="flex items-center gap-2">
                <Dumbbell className="w-2.5 h-2.5 flex-shrink-0" style={{ color: t.accent }} />
                <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', fontWeight: 500, color: t.text }}>{f}</span>
                <div className="flex-1 h-5 rounded-lg" style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '15'}` }} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderExerciseLibrary = () => {
    const library = page.exerciseLibrary || [];
    return (
      <div className="flex-1 overflow-hidden">
        {library.length > 0 ? (
          <div className="space-y-2">
            {library.map((ex, ei) => (
              <div key={ei} className="rounded-lg p-2" style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '10'}` }}>
                <div className="flex items-center gap-2 mb-0.5">
                  <Dumbbell className="w-2.5 h-2.5" style={{ color: t.accent }} />
                  <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', fontWeight: 700, color: t.text }}>{ex.name}</span>
                  {ex.targetMuscles && <span className="text-[7px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: t.accent + '15', color: t.accent }}>{ex.targetMuscles}</span>}
                </div>
                {ex.description && <p style={{ fontFamily: t.bodyFont, fontSize: '0.5rem', color: t.subtext, lineHeight: 1.4 }}>{ex.description}</p>}
                {ex.formTips && <p style={{ fontFamily: t.bodyFont, fontSize: '0.45rem', fontStyle: 'italic', color: t.accent, marginTop: '2px' }}>Tip: {ex.formTips}</p>}
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-1.5">
            {(page.fields || []).map((f, fi) => (
              <div key={fi} className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: t.accent }} />
                <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', color: t.text }}>{f}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderProgress = () => (
    <div className="flex-1 overflow-hidden">
      {page.content && <p className="mb-2" style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', fontStyle: 'italic', color: t.subtext }}>{page.content}</p>}
      <div className="space-y-2">
        {(page.fields || []).map((f, fi) => (
          <div key={fi}>
            <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', fontWeight: 600, color: t.accent, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{f}</span>
            <div className="mt-0.5 h-6 rounded-lg" style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '15'}` }} />
          </div>
        ))}
      </div>
    </div>
  );

  const renderDefault = () => (
    <div className="flex-1 overflow-hidden">
      {page.content && <p className="mb-2 leading-relaxed" style={{ fontFamily: t.bodyFont, fontSize: '0.65rem', color: t.subtext, lineHeight: 1.6 }}>{page.content}</p>}
      {page.fields && page.fields.length > 0 && (
        <div className="space-y-1.5">
          {page.fields.map((f, fi) => (
            <div key={fi} className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: t.accent }} />
              <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', color: t.text, fontWeight: 500, minWidth: '5rem' }}>{f}</span>
              <div className="flex-1 h-6 rounded-lg" style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '15'}` }} />
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const contentMap = {
    'fitness-workout': renderWorkout,
    'fitness-exercise': renderExerciseLibrary,
    'fitness-progress': renderProgress,
    'fitness-milestone': renderProgress,
  };

  const render = contentMap[page.pageType] || renderDefault;

  return (
    <div data-testid={`document-page-${pageIndex}`} className="w-full aspect-[210/297] flex flex-col relative overflow-hidden" style={{ backgroundColor: t.cardBg }}>
      <div className="h-1 w-full" style={{ backgroundColor: meta.color + '40' }} />
      <div className="px-7 pt-4 pb-2 flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Dumbbell className="w-3 h-3" style={{ color: meta.color }} />
            <span className="text-[8px] font-bold tracking-[0.15em] uppercase" style={{ fontFamily: t.bodyFont, color: meta.color }}>{meta.label}</span>
          </div>
          <h2 style={{ fontFamily: t.font, fontWeight: 700, fontSize: 'clamp(1rem, 2.5vw, 1.3rem)', color: t.text, lineHeight: 1.2 }}>{page.title}</h2>
        </div>
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', color: t.subtext, opacity: 0.5 }}>{pageIndex + 1}/{totalPages}</span>
      </div>
      <div className="px-7 flex-1 overflow-hidden flex flex-col">
        {page.content && !contentMap[page.pageType] && <p className="mb-2" style={{ fontFamily: t.bodyFont, fontSize: '0.65rem', color: t.subtext, lineHeight: 1.5 }}>{page.content}</p>}
        {render()}
      </div>
      <div className="px-7 py-2 flex items-center justify-between" style={{ borderTop: `1px solid ${meta.color}10` }}>
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.subtext, opacity: 0.4, letterSpacing: '0.1em' }}>LUMERA STUDIO</span>
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.subtext, opacity: 0.4 }}>Page {pageIndex + 1}</span>
      </div>
    </div>
  );
};

export default FitnessPage;
